"use client";

import {
    createContext,
    useCallback,
    useContext,
    useState,
    type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { getUserEmail } from "@/app/api/service/user";
import Avatar from "@/app/assets/icons/avatar";
import { useCleverTapUser } from "@/app/hooks/use-clevertap";
import { shouldShowPushPromptNow } from "@/app/utils/push-notification-prompt";
import { useAuth } from "@/context/authcontext";
import type { User } from "@/app/interface/user";
import type { StaticNavLink } from "@/config/nav-links";

const SideDrawer = dynamic(() => import("@/app/components/profile-drawer"), {
    ssr: false,
});

type NavProfileContextValue = {
    isAuthenticated: boolean;
    isAuthFetched: boolean;
    openDrawer: () => void;
};

const NavProfileContext = createContext<NavProfileContextValue | null>(null);

type NavProfileProviderProps = {
    children: ReactNode;
};

/**
 * Shares post-auth avatar/drawer state across desktop and mobile header slots
 * without a second getUserEmail fetch (same React Query key as portfolio/unibo).
 */
export function NavProfileProvider({ children }: NavProfileProviderProps) {
    const { isAuthenticated, isAuthFetched } = useAuth();
    const { onUserLogin, isInitialized } = useCleverTapUser();
    const [isOpen, setIsOpen] = useState(false);

    const { data: user } = useQuery<User>({
        queryKey: ["userData"],
        queryFn: getUserEmail,
        enabled: isAuthenticated,
    });

    const openDrawer = useCallback(() => {
        setIsOpen(true);

        if (!user?.userId || !isInitialized || !shouldShowPushPromptNow()) {
            return;
        }

        onUserLogin(
            {
                identity: user.userId,
                name: user.name,
                email: user.email,
                phone: user.phone,
            },
            true,
        );
    }, [isInitialized, onUserLogin, user]);

    return (
        <NavProfileContext.Provider
            value={{ isAuthenticated, isAuthFetched, openDrawer }}
        >
            {children}
            {isAuthenticated && (
                <SideDrawer
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    position="right"
                    name={user?.name}
                    email={user?.email}
                    phone={user?.phone}
                    userId={user?.userId}
                />
            )}
        </NavProfileContext.Provider>
    );
}

export function useNavProfile(): NavProfileContextValue {
    const context = useContext(NavProfileContext);
    if (!context) {
        throw new Error("useNavProfile must be used within a NavProfileProvider");
    }
    return context;
}

/** Same rule as legacy header: avatar only when logged in and not on `/`. */
export function useShowNavAvatar(): boolean {
    const pathname = usePathname();
    const { isAuthenticated } = useNavProfile();
    return Boolean(isAuthenticated && pathname !== "/");
}

/**
 * Filters `authOnly` links (e.g. Portfolio) the same way as the legacy header:
 * visible after login and not on the marketing home (`/`).
 */
export function useVisibleStaticNavLinks(links: StaticNavLink[]): StaticNavLink[] {
    const showPostLoginLinks = useShowNavAvatar();
    return links.filter((link) => !link.authOnly || showPostLoginLinks);
}

/** Avatar entry point — logged-in only; hidden on home (`/`), matching legacy header. */
export function NavAvatarButton() {
    const { openDrawer } = useNavProfile();
    const showAvatar = useShowNavAvatar();

    if (!showAvatar) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={openDrawer}
            aria-label="Open profile"
            className="cursor-pointer"
        >
            <Avatar />
        </button>
    );
}
